import { EllipsisVerticalIcon, TrashIcon } from "lucide-react";
import React from "react";

import { Card, CardContent, CardHeader, PermissionGuard } from "@/components";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirmDialog } from "@/context/confirm-dialog";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";

import { TPlatform } from "../platform.types";

interface IPlatformsTableProps {
  platforms: TPlatform[];
  isLoading: boolean;
  onEditPlatform: (platform: TPlatform) => void;
  onDeletePlatform: (platformId: string) => void;
  isDeleting?: boolean;
}

export const PlatformsTable: React.FC<IPlatformsTableProps> = ({
  platforms,
  isLoading,
  onEditPlatform,
  onDeletePlatform,
  isDeleting = false,
}) => {
  const { openDialog } = useConfirmDialog();
  const { hasAnyPermission } = usePermissions();

  // Verificar se o usuário tem pelo menos uma permissão para ações
  const hasActionPermissions = hasAnyPermission(["platform:update", "platform:delete"]);

  const handleDeletePlatform = (platform: TPlatform) => {
    openDialog(
      "Confirmar exclusão",
      `Tem certeza que deseja excluir a plataforma "${platform.landing_page?.title || 'Sem título'}"? Esta ação não pode ser desfeita.`,
      () => onDeletePlatform(platform.id)
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Plataformas</h3>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Carregando plataformas...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (platforms.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Plataformas</h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              Nenhuma plataforma encontrada para este cliente.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold">
          Plataformas ({platforms.length})
        </h3>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título</TableHead>
              <TableHead>Qtd de Planos</TableHead>
              <TableHead>Data de Criação</TableHead>
              <TableHead>Link</TableHead>
              {hasActionPermissions && (
                <TableHead>Ações</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {platforms.map((platform) => (
              <TableRow key={platform.id}>
                <TableCell className="font-medium">
                  {platform.landing_page?.title}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {platform.plans?.length || 0}{" "}
                    {(platform.plans?.length || 0) === 1 ? "plano" : "planos"}
                  </span>
                </TableCell>
                <TableCell>
                  {new Date(platform.created_at).toLocaleDateString("pt-BR")}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="link"
                    className="text-blue-600 underline hover:text-blue-800 transition-colors p-0 h-auto"
                    onClick={() => {
                      if (platform.landing_page?.lp_link) {
                        window.open(
                          platform.landing_page.lp_link,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }}
                  >
                    Abrir Landing Page
                  </Button>
                </TableCell>
                {hasActionPermissions && (
                  <TableCell>
                    <PermissionGuard
                      permissions={["platform:update", "platform:delete"]}
                      requireAll={false}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size={"sm"} variant={"ghost"} className="p-2">
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="p-auto">
                          <PermissionGuard permission="platform:update">
                            <DropdownMenuItem
                              className="w-full"
                              onClick={() => onEditPlatform(platform)}
                            >
                              {t("generic.edit")}
                            </DropdownMenuItem>
                          </PermissionGuard>
                          <PermissionGuard permission="platform:delete">
                            <DropdownMenuItem
                              className="bg-red-400 w-full hover:bg-red-600"
                              onClick={() => handleDeletePlatform(platform)}
                              disabled={isDeleting}
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              {isDeleting ? "Excluindo..." : t("generic.delete")}
                            </DropdownMenuItem>
                          </PermissionGuard>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </PermissionGuard>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
